import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useFirestoreGeneral } from '../../firebase/useFirestore.js'
import { db } from '../../firebase/config.js'
import useSubtaskStatusChange from '../../hooks/useSubtaskStatusChange.js'
import buildSubtaskDoc from '../../helpers/adressHelpers/buildSubtaskDoc.js'
import { isAppointment } from '../../helpers/adressHelpers/taskKind.js'
import UnplannedSubtasks from './UnplannedSubtasks.jsx'
import { v4 as uuid } from 'uuid'
import { client } from '../../hooks/Client.js'
import { SavedIcon } from "../../StateManagment/SavedIcon.js";
import Location from "../../hooks/Location.js"
import { useContext, useState, useEffect, useMemo } from "react";
import { Auth } from '../../StateManagment/Auth.js'
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import DeleteModal from "../common/DeleteModal.jsx";
import firebase from '../../firebase/compat';
import Table from '../common/table/Table.jsx';
import TableCell from '../common/table/TableCell.jsx';
import TableDragHandleCell from '../common/table/TableDragHandleCell.jsx';
import TableTitleCell from '../common/table/TableTitleCell.jsx';
import ReadOnlyGuard from '../common/ReadOnlyGuard';
import softDeleteDoc from '../../helpers/softDeleteDoc';

/* ------------------ Categorie + klus model ------------------ */

const KLUS_CATEGORIES = [
  {
    category: "Tocht & isolatie",
    items: [
      "Tochtband plaatsen of vervangen (rubber)",
      "Aluminium kaderprofielen plaatsen of vervangen (deur)",
      "Brievenbus voorzien van borstel en afkitten",
      "Tochtstrip plaatsen of vervangen (onder deur)",
      "Kieren en naden afdichten (kit)",
      "Dorpel plaatsen of vervangen",
      "Kozijnfolie aanbrengen op enkel of oud dubbel glas",
    ],
  },
  {
    category: "Verwarming & energie",
    items: [
      "Radiatorfolie plaatsen",
      "Radiatoren bijvullen en ontluchten",
      "Radiatoren reinigen",
      "Waterzijdig inregelen",
      "CV-instellingen optimaliseren",
    ],
  },
  {
    category: "Ventilatie & luchtkwaliteit",
    items: [
      "Ventilatieroosters reinigen",
      "Ventilatiekanalen controleren (luchtdebiet)",
      "Luchtkwaliteitsmeting uitvoeren (CO, CO₂, fijnstof, vocht)",
    ],
  },
  {
    category: "Elektriciteit",
    items: [
      "LED-lampen plaatsen of vervangen",
      "Tijdschakelaar of bewegingssensor plaatsen",
      "Kleine elektraproblemen verhelpen",
    ],
  },
  {
    category: "Waterbesparing & comfort",
    items: [
      "Waterbesparende douchekop plaatsen",
      "Perlator op kraan plaatsen",
      "Vensterbankrooster of radiatorventilator plaatsen",
      "Raamfolie aanbrengen op kleine ruiten (enkelglas)",
      "Advies warmteverlies en comfortmaatregelen",
    ],
  },
  {
    category: "Bewonersondersteuning",
    items: [
      "Energiecoaching geven",
      "Bewonersvragen beantwoorden",
      "Gevaarlijke situaties verhelpen",
      "Ondersteuning contact verhuurder of installateur",
      "Kleine technische klussen bij kwetsbare bewoners",
    ],
  },
];

const SubTasks = ({ task, subTasks }) => {
  // Context
  const [saved, setSaved] = useContext(SavedIcon)
  const [auth] = useContext(Auth)

  // State
  const [dragArray, setDragArray] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteName, setDeleteName] = useState('deze taak')
  const [deleteDocid, setDeleteDocid] = useState('')

  const { runStatusChange } = useSubtaskStatusChange()

  // Hooks
  const adressId = Location()[4]

  // Firestore
  const adress = useFirestoreGeneral('Adresses', 'ID', adressId || '')

  // Alleen opties tonen op address pagina (dus als er een adres geladen is)
  const isAdressPage = Array.isArray(adress) && adress.length > 0

  /* ------------------ standardized options ------------------ */

  // Flat list: "Item - Category"
  const standardizedOptions = useMemo(() => {
    const options = []
    KLUS_CATEGORIES.forEach((c) => {
      c.items.forEach((item) => {
        options.push(`${item} - ${c.category}`)
      })
    })
    return options
  }, [])

  const STANDARDIZED_DATALIST_ID = "standardized-subtask-items-all"

  const parseOptionValue = (rawValue) => {
    const value = (rawValue || '').trim()
    if (!value) return { title: '', category: '' }

    // Split on LAST " - " so titles with hyphens won't break
    const sep = ' - '
    const idx = value.lastIndexOf(sep)
    if (idx === -1) return { title: value, category: '' }

    const title = value.slice(0, idx).trim()
    const category = value.slice(idx + sep.length).trim()
    return { title, category }
  }

  /* ------------------ effects ------------------ */

  // Whenever Firestore data changes, update dragArray state.
  // Skip the update when the snapshot is structurally identical to what we
  // already render — otherwise every keystroke / field write produces a new
  // array reference and re-renders the entire DragDropContext, which causes
  // react-beautiful-dnd to remeasure scroll containers and jumps the page.
  useEffect(() => {
    setDragArray((prev) => {
      const next = subTasks || [];
      if (Array.isArray(prev) && prev.length === next.length) {
        const same = prev.every((p, i) => {
          const n = next[i];
          return (
            p.docid === n.docid &&
            p.Title === n.Title &&
            p.Status === n.Status &&
            p.EstimateMinutes === n.EstimateMinutes &&
            p.Category === n.Category &&
            p.Position === n.Position
          );
        });
        if (same) return prev;
      }
      return next;
    });
  }, [subTasks]);

  /* ------------------ drag & drop ------------------ */

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    setDragArray(items);

    items.forEach((dragItem, idx) => {
      db.collection('Tasks')
        .doc(dragItem.docid)
        .update({ Position: idx + 1 });
    });

    setSaved('flex');
  };

  /* ------------------ CRUD ------------------ */

  // Add new subtask. Het document zelf komt uit buildSubtaskDoc: het soort
  // hangt af van de ouder (huisbezoek of niet) en Parent is nooit null.
  // AI-CONTEXT: See docs/features/subtaken-bij-het-huishouden.md
  const addTask = async () => {
    await db.collection('Tasks').add(buildSubtaskDoc({
      id: uuid(),
      client,
      parent: task,
      adressId,
      userId: auth?.ID,
      position: subTasks?.length ? subTasks.length + 1 : 1,
    }))

    setSaved('flex')
  }

  // Title handler (parses "Title - Category" if user picked a standardized option)
  const titleHandler = async (e) => {
    const docid = e.target.dataset.docid
    const rawValue = e.target.value

    const { title, category } = parseOptionValue(rawValue)

    const payload = { Title: title || rawValue }
    // Only set Category if it's actually present in the chosen option
    if (category) payload.Category = category

    await db.collection('Tasks')
      .doc(docid)
      .update(payload)
      .then(() => setSaved('flex'))
  }

  // ----- Date/Time helpers -----
  const pad2 = (n) => String(n).padStart(2, '0')

  const tsToDateValue = (ts) => {
    if (!ts) return ''
    const d = ts.toDate()
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
  }

  const tsToTimeValue = (ts) => {
    if (!ts) return ''
    const d = ts.toDate()
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
  }

  const buildDateFromInputs = ({ dateStr, timeStr }) => {
    if (!dateStr) return null
    const [y, m, d] = dateStr.split('-').map(Number)
    const [hh, mm] = (timeStr || '00:00').split(':').map(Number)
    return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0)
  }

  const getFallbackDateStr = () => {
    const now = new Date()
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`
  }

  const dateHandler = async (e, t) => {
    const dateStr = e.target.value

    const startTimeStr = tsToTimeValue(t.Timestamp)
    const endTimeStr = tsToTimeValue(t.TimestampEnd)

    const startDate = buildDateFromInputs({ dateStr, timeStr: startTimeStr || '00:00' })
    const endDate = endTimeStr
      ? buildDateFromInputs({ dateStr, timeStr: endTimeStr })
      : null

    await db.collection('Tasks')
      .doc(t.docid)
      .update({
        Timestamp: startDate ? firebase.firestore.Timestamp.fromDate(startDate) : null,
        TimestampEnd: endDate ? firebase.firestore.Timestamp.fromDate(endDate) : null,
      })

    setSaved('flex')
  }

  const startTimeHandler = async (e, t) => {
    const timeStr = e.target.value

    const dateStr =
      tsToDateValue(t.Timestamp) ||
      tsToDateValue(t.TimestampEnd) ||
      getFallbackDateStr()

    const startDate = buildDateFromInputs({ dateStr, timeStr })

    await db.collection('Tasks')
      .doc(t.docid)
      .update({
        Timestamp: startDate ? firebase.firestore.Timestamp.fromDate(startDate) : null,
      })

    setSaved('flex')
  }

  const endTimeHandler = async (e, t) => {
    const timeStr = e.target.value

    if (!timeStr) {
      await db.collection('Tasks').doc(t.docid).update({ TimestampEnd: null })
      setSaved('flex')
      return
    }

    const dateStr =
      tsToDateValue(t.Timestamp) ||
      tsToDateValue(t.TimestampEnd) ||
      getFallbackDateStr()

    const endDate = buildDateFromInputs({ dateStr, timeStr })

    await db.collection('Tasks')
      .doc(t.docid)
      .update({
        TimestampEnd: endDate ? firebase.firestore.Timestamp.fromDate(endDate) : null,
      })

    setSaved('flex')
  }

  // Delete modal
  const deleteModalHandler = (e) => {
    const docid = e.currentTarget.dataset.docid
    const deleteName = e.currentTarget.dataset.deletename

    setDeleteDocid(docid)
    setDeleteName(deleteName)
    setDeleteModal(!deleteModal)
  }

  // Delete task
  const deleteNote = () => {
    softDeleteDoc({ collection: 'Tasks', docid: deleteDocid })
      .then(() => setSaved('flex'))
  }

  // Status handler — delegates stock deduction/restoration to the shared hook.
  const statusHandler = async (e) => {
    const docid = e.target.dataset.docid
    const newStatus = e.target.options[e.target.selectedIndex].value

    const subtask = dragArray.find((s) => s.docid === docid)
    const { ok } = await runStatusChange(subtask, newStatus)
    if (ok) setSaved('flex')
  }

  // Estimated minutes handler
  const estimateMinutesHandler = async (e) => {
    const docid = e.target.dataset.docid
    const value = e.target.value ? parseInt(e.target.value) : null

    await db.collection('Tasks')
      .doc(docid)
      .update({ EstimateMinutes: value })
      .then(() => setSaved('flex'))
  }

  /* ------------------ render ------------------ */

  return (
    <>
      <datalist id={STANDARDIZED_DATALIST_ID}>
        {standardizedOptions.map((opt) => (
          <option key={opt} value={opt} />
        ))}
      </datalist>

      <ReadOnlyGuard edit="tasks.edit">
        {(canEdit) => (
          <>
          <Table
            columns={[
              { key: 'drag', label: '', searchable: false },
              { key: 'title', label: 'TAAK' },
              { key: 'status', label: 'STATUS', searchable: false },
              { key: 'minutes', label: 'GESCHATTE MINUTEN', searchable: false },
              { key: 'delete', label: 'VERWIJDEREN', searchable: false },
            ]}
            items={dragArray || []}
            draggable
            droppableId="subtasks"
            onDragEnd={handleDragEnd}
            getItemKey={(t) => t.ID}
            emptyMessage="Nog geen taken gevonden."
            searchAccessor={(t) => t?.Title || ''}
            searchPlaceholder="Zoeken op taak"
            addRow={canEdit ? {
              toolTipContent: 'Taak toevoegen',
              onClick: addTask,
            } : undefined}
            renderRow={(t) => (
              <>
                <TableDragHandleCell>
                  <DragIndicatorOutlinedIcon />
                </TableDragHandleCell>
                <TableTitleCell
                  value={
                    isAdressPage && t.Category
                      ? `${t.Title || ''} - ${t.Category}`
                      : (t.Title || '')
                  }
                  onBlur={titleHandler}
                  docid={t.docid}
                  placeholder={isAdressPage ? 'Kies of typ een klus (met categorie)' : 'Taak titel'}
                  list={STANDARDIZED_DATALIST_ID}
                />
                <TableCell>
                  <select
                    value={t.Status || 'toDo'}
                    data-docid={t.docid}
                    onChange={statusHandler}
                  >
                    <option value="toDo">Te doen</option>
                    <option value="inProgress">Bezig</option>
                    <option value="done">Klaar</option>
                  </select>
                </TableCell>
                <TableCell>
                  <input
                    type="number"
                    defaultValue={t.EstimateMinutes || ''}
                    data-docid={t.docid}
                    onBlur={estimateMinutesHandler}
                    placeholder="Geschatte minuten"
                    min="0"
                  />
                </TableCell>
                <TableCell>
                  {canEdit && (
                    <DeleteOutlinedIcon
                      className='table-delete-icon'
                      data-id={t.ID}
                      data-docid={t.docid}
                      data-deletename={t.Title}
                      onClick={deleteModalHandler}
                    />
                  )}
                </TableCell>
              </>
            )}
          />
          {/* De losse klussen van het huishouden, om hier alsnog in te plannen.
              Alleen bij een huisbezoek dat al is opgeslagen (docid): anders opent het
              component voor niets een listener, of — in de aanmaakmodus van de agenda —
              koppelt een klik een klus aan een afspraak die bij annuleren nooit ontstaat. */}
          {isAppointment(task) && Boolean(task?.docid) && <UnplannedSubtasks appointment={task} canEdit={canEdit} />}
          </>
        )}
      </ReadOnlyGuard>

      <DeleteModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        deleteItem={deleteNote}
        deleteName={deleteName}
        softDelete
      />
    </>
  )
}

export default SubTasks